import { useStrings } from "features/i18n/useStrings";
import strings from "./FormOutline.strings";
import {
    selectQuestionGroup,
    selectEnableReportingViewRedirects,
    selectQuestionGroupIds,
} from "features/qualityForm/qualityFormSlice";
import {
    selectQuestion,
    selectIsSurveyOrEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { LevelContext } from "features/qualityForm/common/Contexts";
import { useContext } from "react";
import "./FormOutline.less";
import { TreeNode } from "../common/components/TreeNode";
import {
    QuestionGroupSettingsIndicators,
    QuestionSettingsIndicators,
} from "features/qualityForm/common/questionComponents/SettingsIndicators";
import { useAppSelector } from "app/hooks";
import {
    selectIsQuestionGroupValid,
    selectIsQuestionGroupValidIgnoringUntouchedEmpties,
    selectIsQuestionGroupQuestionCountValid,
    selectIsQuestionGroupNameValid,
    selectIsQuestionValid,
    selectIsQuestionValidIgnoringUntouchedEmpties,
    selectIsQuestionNameValid,
    selectIsAnswerOptionValid,
    selectIsFollowUpExplanationValid,
} from "features/qualityForm/qualityFormValidations";
import {
    GuxTruncate,
    GuxButton,
    GuxIcon,
} from "genesys-spark-components-react";
import { MarkdownInline } from "features/common/Markdown";
import { SurveyQuestionTypeEnum } from "@genesys/cloud-frontend/models";

function QuestionOutlineSummary({ id }: { id: string }) {
    const t = useStrings(strings);
    const questionText = useAppSelector(
        (state) => selectQuestion(state, id)?.text ?? t.labels.newQuestion
    );

    return (
        <div className="content-box-data">
            <div className="question-content-text">
                <span className="small-body">
                    <GuxTruncate max-lines={15}>
                        <MarkdownInline
                            markdownInlineText={questionText}
                        ></MarkdownInline>
                    </GuxTruncate>
                </span>
            </div>
            <QuestionSettingsIndicators questionId={id} />
        </div>
    );
}

function QuestionGroupOutlineSummary({ id }: { id: string }) {
    const t = useStrings(strings);

    const name =
        useAppSelector((state) => selectQuestionGroup(state, id)?.name) ??
        t.labels.newQuestionGroup;
    const numQuestions = useAppSelector(
        (state) => selectQuestionGroup(state, id)?.questions?.length ?? 0
    );

    return (
        <div className="content-box-data">
            <div className="group-content-text">
                <span className="small-body">
                    <GuxTruncate max-lines={1}>
                        <MarkdownInline
                            markdownInlineText={name}
                        ></MarkdownInline>
                    </GuxTruncate>
                </span>
                <div className="light-font">
                    {t.labels.questionCount({ questionCount: numQuestions })}
                </div>
            </div>
            <QuestionGroupSettingsIndicators questionGroupId={id} />
        </div>
    );
}
function QuestionOutline({ id }: { id: string }) {
    const { level, subLevel } = useContext(LevelContext);

    const isSurveyOrEvaluation = useAppSelector(selectIsSurveyOrEvaluationForm);
    const isQuestionValid = useAppSelector((state) =>
        selectIsQuestionValid(state, id)
    );
    const isQuestionValidIgnoringUntouchedEmpties = useAppSelector((state) =>
        selectIsQuestionValidIgnoringUntouchedEmpties(state, id)
    );
    const isQuestionNameTouched = useAppSelector(
        (state) => state.qualityForm.data.touchedQuestionNames[id]
    );
    const isQuestionNameValid = useAppSelector((state) =>
        selectIsQuestionNameValid(state, id)
    );
    const hasInvalidTouchedAnswer = useAppSelector((state) => {
        const question = state.qualityForm.data.questions?.[id];
        const answerIds = question?.answerOptions ?? [];
        const multiSelectIds = question?.multipleSelectOptionQuestions ?? [];
        const touched = state.qualityForm.data.touchedAnswerOptions;
        return (
            answerIds.some(
                (optionId) =>
                    touched[optionId] &&
                    !selectIsAnswerOptionValid(state, optionId)
            ) ||
            multiSelectIds.some(
                (optionId) =>
                    touched[optionId] &&
                    !selectIsAnswerOptionValid(state, optionId, true)
            )
        );
    });
    const question = useAppSelector((state) => selectQuestion(state, id));
    const isFollowUpExplanationTouched = useAppSelector(
        (state) => state.qualityForm.data.touchedNpsFollowUpQuestionPrompt
    );
    const isFollowUpExplanationValid = useAppSelector((state) =>
        selectIsFollowUpExplanationValid(state, id)
    );
    const showError = isSurveyOrEvaluation
        ? !isQuestionValidIgnoringUntouchedEmpties ||
          (!isQuestionNameValid && isQuestionNameTouched) ||
          hasInvalidTouchedAnswer ||
          (question?.type === SurveyQuestionTypeEnum.NpsQuestion &&
              isFollowUpExplanationTouched &&
              !isFollowUpExplanationValid)
        : !isQuestionValid;

    const canViewQuestionDetailsView = useAppSelector(
        selectEnableReportingViewRedirects
    );
    const questionOutline = (
        <QuestionOutlineSummary id={id}></QuestionOutlineSummary>
    );

    const t = useStrings(strings);

    const questionDetailsViewButton = (
        <GuxButton aria-label={t.labels.icon}>
            <GuxIcon iconName="file-report" decorative={true}></GuxIcon>
        </GuxButton>
    );

    return (
        <TreeNode
            index={t.indices.questionIndex({
                level: level,
                subIndex: subLevel,
            })}
            treeNodeCustomContent={questionOutline}
            {...(canViewQuestionDetailsView && {
                treeNodeCustomIcon: questionDetailsViewButton,
            })}
            hasCustomIcon={canViewQuestionDetailsView}
            hasError={showError}
        />
    );
}

function QuestionGroupOutline({ id }: { id: string }) {
    const { level } = useContext(LevelContext);
    const questionIds =
        useAppSelector((state) => selectQuestionGroup(state, id)?.questions) ??
        [];
    const isSurveyOrEvaluation = useAppSelector(selectIsSurveyOrEvaluationForm);
    const isQuestionGroupValid = useAppSelector((state) =>
        selectIsQuestionGroupValid(state, id)
    );
    const isValidIgnoringUntouchedEmpties = useAppSelector((state) =>
        selectIsQuestionGroupValidIgnoringUntouchedEmpties(state, id)
    );
    const isQuestionGroupCountTouched = useAppSelector(
        (state) => state.qualityForm.data.touchedGroupQuestionCounts[id]
    );
    const isQuestionGroupCountValid = useAppSelector((state) =>
        selectIsQuestionGroupQuestionCountValid(state, id)
    );
    const isQuestionGroupNameTouched = useAppSelector(
        (state) => state.qualityForm.data.touchedGroupNames[id]
    );
    const isQuestionGroupNameValid = useAppSelector((state) =>
        selectIsQuestionGroupNameValid(state, id)
    );
    const showError = isSurveyOrEvaluation
        ? !isValidIgnoringUntouchedEmpties ||
          (isQuestionGroupNameTouched && !isQuestionGroupNameValid) ||
          (isQuestionGroupCountTouched && !isQuestionGroupCountValid)
        : !isQuestionGroupValid;
    const canViewQuestionDetailsView = useAppSelector(
        selectEnableReportingViewRedirects
    );

    const questions = questionIds.map((questionId, index) => {
        const questionOutline = (
            <LevelContext.Provider
                value={{ level: level, subLevel: index + 1 }}
            >
                <QuestionOutline id={questionId}></QuestionOutline>
            </LevelContext.Provider>
        );
        return (
            <div
                className="QuestionOutline"
                key={questionId}
                data-testid="question-outline"
            >
                {questionOutline}
            </div>
        );
    });

    const questionGroupData = (
        <QuestionGroupOutlineSummary id={id}></QuestionGroupOutlineSummary>
    );

    return (
        <div className="QuestionGroupOutline">
            <TreeNode
                index={level.toString()}
                treeNodeCustomContent={questionGroupData}
                hasCustomIcon={canViewQuestionDetailsView}
                hasError={showError}
            >
                {questions}
            </TreeNode>
        </div>
    );
}

export function FormOutline() {
    const questionGroupIds = useAppSelector(selectQuestionGroupIds);
    const questionGroups = questionGroupIds.map((questionGroupId, index) => {
        return (
            <LevelContext.Provider
                key={questionGroupId}
                value={{ level: index + 1, subLevel: 0 }}
            >
                <QuestionGroupOutline
                    id={questionGroupId}
                    key="questionGroupId"
                ></QuestionGroupOutline>
            </LevelContext.Provider>
        );
    });
    return (
        <div className="FormOutline" data-testid="form-outline" role="tree">
            {questionGroups}
        </div>
    );
}
